import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_TOKEN_KEY } from '../config/api';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://sanduk.gurusoftware.cloud';

// ── Context ────────────────────────────────────────────────────────────
const SocketContext = createContext(null);

export const useSocket = () => useContext(SocketContext);

// ── Provider ───────────────────────────────────────────────────────────
export const SocketProvider = ({ children }) => {
  const socketRef  = useRef(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(API_TOKEN_KEY);
    if (!token) return;

    const socket = io(SOCKET_URL, {
      path: '/socket.io/',
      auth: { token },
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));
    socket.on('connect_error', (err) => {
      console.warn('[Socket] connection error:', err.message);
      setConnected(false);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  /**
   * Subscribe to a socket event. Returns an unsubscribe function.
   * Automatically removes the listener when the calling component unmounts.
   */
  const on = useCallback((event, handler) => {
    const s = socketRef.current;
    if (!s) return () => {};
    s.on(event, handler);
    return () => s.off(event, handler);
  }, []);

  return (
    <SocketContext.Provider value={{ socketRef, connected, on }}>
      {children}
    </SocketContext.Provider>
  );
};
